import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
    Dialog,
    DialogTitle,
    Divider,
    Button,
    IconButton,
    Typography,
    Tabs,
    Tab,
    Box,
    Grid
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {
    Timeline,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent
} from '@material-ui/lab';
import './ModalAssetInformation.scss';
import { getImageURL, GetTranslatedValue } from '../../utils';

const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
        {value === index && <Box mt={2}>{children}</Box>}
    </div>
);

const renderFieldValue = (field, fieldMap, labels = {}) => {
    const { content, values } = field;
    const invalidFormulaLabel = labels.invalidFormula || 'Invalid Formula';

    if (!values) return '-';

    switch (content) {
        case 'singleLine':
        case 'email':
        case 'url':
        case 'currency':
        case 'percentage':
        case 'decimal':
        case 'dateTime':
        case 'date':
            return values.initialValue || '-';
        case 'formula': {
            const formula = values.initialValue || '';
            if (!formula) return '-';

            const replaced = formula.replace(/\b[a-z0-9]{12}\b/gi, match => {
                let val = fieldMap[match];
                if (typeof val === 'string') {
                    val = val.replace(/,/g, '');
                }
                return val !== undefined && !isNaN(Number(val)) ? `(${val})` : '0';
            });

            try {
                const result = eval(replaced);
                return isNaN(result) ? '-' : Number(result).toLocaleString();
            } catch {
                return invalidFormulaLabel;
            }
        }

        case 'multiLine':
            return <Typography variant="body2">{values.initialValue}</Typography>;

        case 'richText':
            return <div dangerouslySetInnerHTML={{ __html: values.initialValue }} />;

        case 'imageUpload':
            return (
                <img
                    src={getImageURL(values.fileName, 'customFields', values.initialValue)}
                    alt={'Imagen'}
                    style={{ maxWidth: '100%', height: 'auto', width: '200px' }}
                />
            );

        case 'fileUpload': {
            const { fileId, fileExt, fileName } = values;
            if (fileId && fileExt) {
                const href = getImageURL(fileId, 'customFields', fileExt);
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {fileName || 'Descargar archivo'}
                    </a>
                );
            }
            return values.initialValue ? (
                <a href={values.initialValue} target="_blank" rel="noopener noreferrer">
                    Descargar archivo
                </a>
            ) : '-';
        }

        case 'dropDown':
            return values.initialValue || '-';

        case 'radioButtons': {
            const { options = [], selectedItem } = values;
            if (!selectedItem) return '-';
            const selectedIndex = Number(String(selectedItem).replace(/^rad/i, '')) - 1;
            return options[selectedIndex] || '-';
        }

        case 'decisionBox':
        case 'checkboxes':
            return (values.selectedOptions || []).join(', ') || '-';

        default:
            return '-';
    }
};

const renderTabDataAsTables = (tabsData, fieldMap, labels = {}) => {
    if (!tabsData) return '';

    return Object.entries(tabsData).map(([tabKey, tabObj]) => {
        const fields = [...(tabObj.left || []), ...(tabObj.right || [])];

        return `
            <h3 style="margin-top: 30px;">${tabObj.info?.name || 'Sección'}</h3>
            <table>
                <tbody>
                    ${fields.map(field => {
            const fieldName = field.values?.fieldName || '-';
            let value = renderFieldValue(field, fieldMap, labels);

            if (React.isValidElement(value)) {
                // Si es una imagen
                if (value.type === 'img' && value.props?.src) {
                    value = `<img src="${value.props.src}" alt="${value.props.alt || ''}" style="max-width: 200px;" />`;
                } else if (value.type === 'a' && value.props?.href) {
                    const label =
                        typeof value.props.children === 'string'
                            ? value.props.children
                            : 'Descargar archivo';
                    value = `<a href="${value.props.href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
                } else if (value.props?.dangerouslySetInnerHTML) {
                    value = value.props.dangerouslySetInnerHTML.__html;
                } else if (typeof value.props?.children === 'string') {
                    value = value.props.children;
                } else {
                    value = '-';
                }
            }

            return `
                    <tr>
                        <td><strong>${fieldName}</strong></td>
                        <td>${typeof value === 'string' ? value : ''}</td>
                    </tr>
                `;
        }).join('')}
                </tbody>
            </table>
        `;
    }).join('');
};


const ModalAssetInformation = ({ open, onClose, assetData }) => {
    const intl = useIntl();
    const [selectedTab, setSelectedTab] = useState(0);
    const {
        name,
        brand,
        model,
        category,
        status,
        serial,
        notes,
        quantity,
        purchase_date,
        purchase_price,
        price,
        total_price,
        EPC,
        locationPath,
        creator,
        creationDate,
        labeling_user,
        labeling_date,
        responsible,
        assignedTo,
        reconciled,
        history,
        imageURL,
        tabsData,
        children
    } = assetData;

    const responsibleDisplay =
        (typeof assignedTo === 'string' && assignedTo) ||
        (typeof responsible === 'string' && responsible) ||
        '';

    const reconciledDisplay = reconciled
        ? intl.formatMessage({ id: 'GENERAL.CAPTION.TRUE', defaultMessage: 'Yes' })
        : intl.formatMessage({ id: 'GENERAL.CAPTION.FALSE', defaultMessage: 'No' });

    const fieldLabels = {
        invalidFormula: intl.formatMessage({
            id: 'ASSETS.MODAL.INVALID.FORMULA',
            defaultMessage: 'Invalid Formula'
        })
    };

    const printInformation = () => {
        const contentInformation = document.getElementById('asset-information-content');
        const contentHistory = document.getElementById('asset-history-content');
        const contentChildren = document.getElementById('asset-children-content');
        const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map(link => link.outerHTML).join('');

        const tabTablesHTML = renderTabDataAsTables(tabsData, fieldMap, fieldLabels);

        const styles = `
            <style>
                body {
                font-family: Arial, sans-serif;
                padding: 20px;
                background-color: #fff !important;
                color: #000 !important;
                }
                table {
                width: 80% !important;
                border-collapse: collapse;
                font-size: 14px;
                align-self: center;
                }
                table td {
                padding: 8px;
                border: 1px solid #ccc;
                }
                h2, h3 {
                margin-top: 20px;
                text-align: center;
                color: #000 !important;
                }
                img {
                max-width: 100%;
                }
            </style>
        `;

        const win = window.open('', '_blank');
        win.document.write(`
      <html>
        <head>
        <title>Información del Activo</title>
            ${styles}
            ${styleLinks}
        </head>
        <body>
            ${contentInformation.innerHTML}
            ${contentChildren.innerHTML}
            ${contentHistory.innerHTML}
            ${tabTablesHTML}
        </body>
      </html>
    `);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 500);
    };

    const fieldMap = {};
    Object.values(tabsData || {}).forEach(tab => {
        [...(tab.left || []), ...(tab.right || [])].forEach(field => {
            if (field?.id && field?.values?.initialValue !== undefined) {
                fieldMap[field.id] = field.values.initialValue;
            }
        });
    });

    return (
        <Dialog open={open} onClose={onClose} className="modal-asset-information" fullWidth maxWidth="md">
            <div className="modal-content-wrapper">
                <DialogTitle disableTypography className="modal-title">
                    <Typography variant="h6">{GetTranslatedValue('ASSETS.MODAL.DOWNLOAD.ASSET.INFORMATION.TITLE', 'Asset Information')}</Typography>
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </DialogTitle>
                <Divider />
                <div className="modal-body">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={printInformation}
                        style={{ margin: '20px 0' }}
                    >
                        {GetTranslatedValue('ASSETS.MODAL.DOWNLOAD.PRINT.EXPORT', 'Print / Export PDF')}
                    </Button>

                    <div id="asset-information-content" className="information-body">
                        <h3 style={{ textAlign: 'center' }}>{GetTranslatedValue('INVENTORIES.ASSET.NAME', 'Asset name')}</h3>
                        <h3 style={{ textAlign: 'center' }}>{name}</h3>
                        {imageURL && (
                            <div style={{ margin: '10px 0', textAlign: 'center' }}>
                                <img src={imageURL} alt="Activo" width="200" />
                            </div>
                        )}
                        <h3 style={{ textAlign: 'center', marginTop: 30 }}>{GetTranslatedValue('ASSETS.MODAL.DOWNLOAD.GENERAL.DATA', 'General Data')}</h3>
                        <table style={{ width: '100%', marginTop: 20 }}>
                            <tbody>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.BRAND', 'Brand')}:</strong></td><td>{brand}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.MODEL', 'Model')}:</strong></td><td>{model}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('GENERAL.CAPTION.CATEGORY', 'Category')}:</strong></td><td>{category}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('GENERAL.CAPTION.STATUS', 'Status')}:</strong></td><td>{status}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.SERIAL', 'Serial')}:</strong></td><td>{serial}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.NOTES', 'Notes')}:</strong></td><td>{notes}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.QUANTITY', 'Quantity')}:</strong></td><td>{quantity}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.PURCHASEDATE', 'Purchase Date')}:</strong></td><td>{purchase_date}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.PURCHASEPRICE', 'Purchase Price')}:</strong></td><td>{purchase_price}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('GENERAL.CAPTION.PRICE', 'Price')}:</strong></td><td>{price}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.TOTALPRICE', 'Total Price')}:</strong></td><td>{total_price}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.EPC', 'EPC')}:</strong></td><td>{EPC}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSET.REPORT.LOCATION.PATH', 'Location Path')}:</strong></td><td>{locationPath}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('RECORD.CAPTION.CREATOR', 'Creator')}:</strong></td><td>{creator}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('GENERAL.CAPTION.CREATION.DATE', 'Creation Date')}:</strong></td><td>{creationDate}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.LABELINGUSER', 'Labeling User')}:</strong></td><td>{labeling_user}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.LABELINGDATE', 'Labeling Date')}:</strong></td><td>{labeling_date}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.RESPONSIBLE', 'Responsible')}:</strong></td><td>{responsibleDisplay}</td></tr>
                                <tr><td><strong>{GetTranslatedValue('ASSETS.CAPTION.RECONCILED', 'Reconciled')}:</strong></td><td>{reconciledDisplay}</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <div id="asset-children-content" className="information-body">
                        {children && children.length > 0 && (
                            <>
                                <h3 style={{ textAlign: 'center', marginTop: 30 }}>{GetTranslatedValue('ASSETS.MODAL.TAB.OTHER.CHILDREN', 'Children')}</h3>
                                <table style={{ width: '100%', marginTop: 20 }}>
                                    <tbody>
                                        <tr>
                                            <th>{GetTranslatedValue('ASSETS.CAPTION.NAME', 'Name')}</th>
                                            <th>{GetTranslatedValue('ASSETS.CAPTION.BRAND', 'Brand')}</th>
                                            <th>{GetTranslatedValue('ASSETS.CAPTION.MODEL', 'Model')}</th>
                                            <th>{GetTranslatedValue('ASSETS.CAPTION.EPC', 'EPC')}</th>
                                            <th>{GetTranslatedValue('ASSETS.CAPTION.SERIAL', 'Serial')}</th>
                                            <th>{GetTranslatedValue('GENERAL.CAPTION.CREATION.DATE', 'Creation Date')}</th>
                                        </tr>
                                        {children?.map((child, idx) => {
                                            return (
                                                <tr key={idx}>
                                                    <td><strong>{child.name}</strong></td>
                                                    <td>{child.brand}</td>
                                                    <td>{child.model}</td>
                                                    <td>{child.EPC}</td>
                                                    <td>{child.serial}</td>
                                                    <td>{child.creationDate}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>

                    <div id="asset-history-content" className="information-body">
                        <h3 style={{ textAlign: 'center', marginTop: 30 }}>{GetTranslatedValue('ASSETS.MODAL.HISTORY', 'History')}</h3>
                        {Array.isArray(history) && history.length > 0 ? (
                            <Timeline align="left">
                                {history.map(({ processName, processType, date, label, processId }, idx) => (
                                    <TimelineItem key={idx}>
                                        <TimelineOppositeContent>
                                            <Typography variant="body1">{processName}</Typography>
                                            <Typography variant="body2" color="textSecondary">Folio: {processId}</Typography>
                                            <Typography variant="body2" color="textSecondary">{processType}</Typography>
                                            <Typography variant="body2" color="textSecondary">{date}</Typography>
                                        </TimelineOppositeContent>
                                        <TimelineSeparator>
                                            <TimelineDot />
                                            {idx !== history.length - 1 && <TimelineConnector />}
                                        </TimelineSeparator>
                                        <TimelineContent>
                                            <Typography variant="body1">{label}</Typography>
                                        </TimelineContent>
                                    </TimelineItem>
                                ))}
                            </Timeline>
                        ) : (
                            <Typography color="textSecondary">Historial disponible.</Typography>
                        )}
                    </div>

                    <div id="asset-custom-fields-content" className="information-body">
                        {tabsData && Object.keys(tabsData).length > 0 && (
                            <div style={{ marginTop: 40 }}>
                                <h3 style={{ textAlign: 'center', marginTop: 30 }}>{GetTranslatedValue('ASSETS.MODAL.DOWNLOAD.ADDITIONAL.DATA', 'Additional Data')}</h3>
                                <Tabs
                                    value={selectedTab}
                                    onChange={(e, newVal) => setSelectedTab(newVal)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    {Object.entries(tabsData).map(([tabKey, tabObj], idx) => (
                                        <Tab key={tabKey} label={tabObj.info?.name || `Pestaña ${idx + 1}`} />
                                    ))}
                                </Tabs>

                                {Object.entries(tabsData).map(([tabKey, tabObj], idx) => (
                                    <TabPanel key={tabKey} value={selectedTab} index={idx}>
                                        <Grid container spacing={2} alignItems="flex-start">
                                            {[...(tabObj.left || []), ...(tabObj.right || [])].map((field, i) => (
                                                <Grid item xs={6} key={field.id}>
                                                    <Box
                                                        border={1}
                                                        borderColor="#ccc"
                                                        borderRadius={4}
                                                        p={2}
                                                        style={{ height: 'auto', display: 'block' }}
                                                    >
                                                        <Typography variant="body2"><strong>{field.values?.fieldName}</strong></Typography>
                                                        <div>{renderFieldValue(field, fieldMap, fieldLabels)}</div>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </TabPanel>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default ModalAssetInformation;
